import type { DragEndEvent } from "@dnd-kit/core";

import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Button, ButtonTypes, Separator } from "@repo/ui";
import { Plus } from "lucide-react";
import { useState } from "react";

import { useMapConfig } from "./config/mapConfigContext";
import { MapConfigSelector } from "./config/mapSelector";
import { isLayerVisible } from "./layers/layer";
import { useLayerResults, useMapLayers } from "./layers/layerContext";
import { SortableLayerItem } from "./layers/sortableLayerItem";
import style from "./layers/style.module.scss";

export function MapSidebar({ onExecute }: { onExecute?: () => void }) {
	const { layers, addLayer, updateLayer, reorderLayers } = useMapLayers();
	const results = useLayerResults();
	const { mapConfigId, isTransitioning } = useMapConfig();
	const [editingLayerId, setEditingLayerId] = useState<string | null>(null);

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);

	const handleAddLayer = () => {
		const layer = addLayer({ itemType: "RetailLocations" });
		setEditingLayerId(layer.id);
	};

	const toggleLayerVisibility = (id: string) => {
		updateLayer(id, (layer) => ({ ...layer, visible: !isLayerVisible(layer) }));
	};

	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over || active.id === over.id) return;

		const oldIndex = layers.data.findIndex((l) => l.id === active.id);
		const newIndex = layers.data.findIndex((l) => l.id === over.id);
		if (oldIndex !== -1 && newIndex !== -1) {
			reorderLayers(oldIndex, newIndex);
		}
	};

	return (
		<div className={style.root}>
			<div className={style.content}>
				<MapConfigSelector onExecute={onExecute}>
					<section>
						<Separator className="my-4" />

						<div className="my-2 flex items-center justify-between">
							<h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
								Active Layers
							</h2>
							{!!mapConfigId && (
								<Button
									types={[ButtonTypes.Primary]}
									onClick={handleAddLayer}
									className="flex items-center justify-center"
								>
									<Plus className="h-4 w-4" /> Add Layer
								</Button>
							)}
						</div>

						<div className="space-y-2">
							{isTransitioning ? (
								<EmptyState>Loading configuration...</EmptyState>
							) : (
								<>
									{layers.data.length === 0 && <EmptyState>No layers added yet.</EmptyState>}
									<DndContext
										sensors={sensors}
										collisionDetection={closestCenter}
										onDragEnd={handleDragEnd}
									>
										<SortableContext
											items={layers.data.map((l) => l.id)}
											strategy={verticalListSortingStrategy}
										>
											{layers.data.map((layer) => (
												<SortableLayerItem
													key={layer.id}
													layer={layer}
													result={results.get(layer.id)}
													isEditing={editingLayerId === layer.id}
													onEdit={() => setEditingLayerId(layer.id)}
													onCloseEdit={() => setEditingLayerId(null)}
													onToggleVisibility={() => toggleLayerVisibility(layer.id)}
												/>
											))}
										</SortableContext>
									</DndContext>
								</>
							)}
						</div>
					</section>
				</MapConfigSelector>
			</div>
		</div>
	);
}

function EmptyState({ children }: { children: React.ReactNode }) {
	return (
		<div className="rounded-lg border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
			{children}
		</div>
	);
}
